import type { JSX } from "react";
import type { PostMetrics } from "../api";
import { formatCount } from "../lib/metrics";

const svg = (children: JSX.Element) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

/** Ordered display config: which PostMetrics field, its icon, and its label. */
const PARTS: { key: keyof PostMetrics; icon: JSX.Element; label: string }[] = [
  {
    key: "views",
    label: "views",
    icon: svg(
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
      </>,
    ),
  },
  {
    key: "likes",
    label: "likes",
    icon: svg(
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />,
    ),
  },
  {
    key: "comments",
    label: "comments",
    icon: svg(<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-4-.9L3 21l1.9-4a8.4 8.4 0 0 1-.9-4 8.4 8.4 0 0 1 8.4-8.4h.5A8.4 8.4 0 0 1 21 11v.5Z" />),
  },
  {
    key: "shares",
    label: "shares",
    icon: svg(
      <>
        <polyline points="17 1 21 5 17 9" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <polyline points="7 23 3 19 7 15" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </>,
    ),
  },
];

/**
 * Compact engagement chips for one published leg (views · likes · comments ·
 * shares). Renders nothing when there are no metrics — absence shows nothing,
 * never zeros, so an unsupported platform (TikTok) or a not-yet-refreshed post
 * is simply blank rather than "0 views".
 */
export function MetricsChips({ metrics }: { metrics: PostMetrics | undefined }) {
  if (!metrics) return null;
  const shown = PARTS.filter((p) => typeof metrics[p.key] === "number");
  if (shown.length === 0) return null;
  return (
    <span className="metrics" aria-label="Engagement">
      {shown.map((p) => (
        <span key={p.key} className="metric" title={p.label}>
          <span className="metric-glyph" aria-hidden="true">
            {p.icon}
          </span>
          <span className="metric-value">{formatCount(metrics[p.key] as number)}</span>
        </span>
      ))}
    </span>
  );
}
